/**
 * The client-safe half of a person's Time off tab: the shape the API returns,
 * plus the pure helpers the UI needs over it.
 *
 * Separate from `src/api/person-pto.ts` for a hard reason, not tidiness. That
 * module reaches the gateway and the database, so it imports the logger, drizzle
 * and the gateway client. A component that imports a VALUE from it (a type-only
 * import is erased and harmless) drags that whole server graph into the browser
 * bundle, where `process` does not exist — which blanks the entire app on load.
 * Anything the client needs at runtime belongs here instead.
 */

import { HOURS_PER_DAY, PTO_TYPES, type PtoType } from "@/lib/pto";
import type { PtoBucketSummary } from "@/lib/pto-balance";

/** Whether a row's hours are booked fact or arithmetic. */
export type PersonLeaveStatus = "approved" | "pending";

/** One absence on a person's profile — an approved day, or a pending request. */
export interface PersonLeaveItem {
  /** Stable list key. Not an id: an approved day has none in NOS. */
  key: string;
  status: PersonLeaveStatus;
  type: PtoType;
  /** Date-only ISO, inclusive. Equal for an approved single day. */
  startDate: string;
  endDate: string;
  hours: number;
  /**
   * True when `hours` was derived from a request's range rather than read from
   * booked entries — weekdays × hours-per-day, with company holidays NOT
   * deducted, so it can read a day high.
   */
  estimated: boolean;
  /** Pending only — `TimeEntry` records neither for approved leave. */
  approverName: string | null;
  description: string | null;
}

export interface PersonPto {
  items: PersonLeaveItem[];
  /**
   * Allowance / accrued / used / pending per bucket for the CURRENT calendar
   * year, whatever the list shows. The list is upcoming-only; a balance that
   * ignored the year already spent would be meaningless.
   */
  summary: PtoBucketSummary[];
  /** The year `summary` covers, so the UI can label it rather than assume. */
  summaryYear: number;
  /**
   * Whether `summary` holds real balances or was withheld from this viewer.
   *
   * The year's allowances, what has been used and what is left are visible only
   * to the person, their direct manager and admins; upcoming leave is visible
   * to anyone signed in (WI-148). False means "you may not see this", which the
   * tab has to say — an empty `summary` alone would read as "no leave
   * recorded", which is a different and wrong statement.
   */
  canViewBalances: boolean;
  /**
   * False when the server has no gateway credentials, so the tab can explain
   * itself instead of looking like someone with no leave booked.
   */
  connected: boolean;
  /**
   * True when approved leave loaded but the pending read failed. The tab says
   * so rather than implying nothing is awaiting approval.
   */
  pendingUnavailable: boolean;
}

/** One type's share of a person's upcoming leave, for a filter chip. */
export interface LeaveTypeTally {
  type: PtoType;
  /** Days behind the chip — approved hours and estimated pending days alike. */
  days: number;
  /** How many lines the chip would hide. */
  lines: number;
}

/**
 * The leave types present in a list, in `PTO_TYPES` display order.
 *
 * Only types actually present: a person's upcoming leave is usually one or two
 * of the five, and chips for the rest are noise. Callers must derive these from
 * the UNFILTERED list, or a chip vanishes the moment it is used and there is no
 * way to turn it back on.
 */
export function leaveTypeBreakdown(items: PersonLeaveItem[]): LeaveTypeTally[] {
  const byType = new Map<PtoType, { days: number; lines: number }>();
  for (const item of items) {
    const tally = byType.get(item.type) ?? { days: 0, lines: 0 };
    tally.days += item.hours / HOURS_PER_DAY;
    tally.lines += 1;
    byType.set(item.type, tally);
  }

  // Iterate the canonical order rather than the map's insertion order, so the
  // chips never reshuffle between people or renders.
  return PTO_TYPES.filter((type) => byType.has(type)).map((type) => {
    const tally = byType.get(type);
    return { type, days: Number((tally?.days ?? 0).toFixed(2)), lines: tally?.lines ?? 0 };
  });
}
